import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { DEFAULT_LOCALE } from "./util/LocalePreferences";

const resources = {
    "en-US": {
        translation: {
            "applicationName": "CALI - Customer Account Lookup Interface",
            "common" : {
                "lookup" : "Lookup",
                "clear" : "Clear",
                "locale" : "Locale",
                "delete"  : "Delete",
                "cancel" : "Cancel"
            },
            "domain" : {
                "common" : {
                    "region" : "Region",
                    "customerAccountNumber" : "Customer Account Number",
                    "customerPhoneNumber" : "Customer Phone Number",
                    "accountNumber" : "Account Number",
                    "regionalAccount" : "Regional Account",
                    "identityProvider" : "Identity Provider",
                    "signIn" : "Sign In",
                    "children" : "Children",
                    "products" : "Products",
                    "displayName" : "Display Name",
                    "affinity" : "Backend",
                    "created" : "Created",
                    "globalAccountId" : "Global Account Id",
                    "accountHealth" : "Account Health",
                    "signInSessionsValidFrom" : "Sessions Valid From",
                    "membership" : "Membership",
                    "bonusEntitlements" : "Bonus Entitlements"
                },
                "productLines": {
                    "STEP_BY_STEP": "Step By Step",
                    "LETS_PLAY": "Let's Play",
                    "STRAIGHT_PLAY" : "Straight Play",
                    "PLAY_ALONG" : "Play Along!",
                    "LETS_READ" : "Let's Read",
                    "SING_ALONG"  : "Sing Along",
                    "ENGLISH_TAB" : "English Tab",

                    "PLAY_ALONG_AUDIO" : "Play Along! (Audio)",
                    "SING_ALONG_AUDIO" : "Sing Along! (Audio)",

                    "SING_ALONG_SPOKEN_WORD_AUDIO" : "Sing Along! (Spoken Word Audio)",
                    "STORIES_AND_SONGS_AUDIO" : "Stories and Songs (Audio)",

                    "BEDTIME_REVIEW" : "Bedtime Review",

                    "BEDTIME_REVIEW_AUDIO" : "Bedtime Review (Audio)",
                    "LISTEN_ALONG_AUDIO" : "Listen Along (Audio)",

                    "STEP_BY_STEP_TEASER" : "Step by Step (Teaser)",
                    "STRAIGHT_PLAY_TEASER" : "Straight Play (Teaser)",
                    "LETS_PLAY_TEASER" : "Let's Play (Teaser)"

                },
                "signInActivity" : {
                    "id": "id",
                    "activity" : "Activity",
                    "ipAddress" : "IP Address",
                    "timestamp" : "Timestamp",
                    "appId" : "Application Id",
                    "appName" : "Application Name",
                    "daysAgo" : "Day Ago",
                    "activities" : {
                        "ADD_USER" : "Create User",
                        "AUTHENTICATION" : "Authentication",
                        "ACCESS_TOKEN" : "Access Token Issued",
                        "ACCESS_TOKEN_EXCHANGE" : "Access Token Exchange",
                        "REFRESH_TOKEN_EXCHANGE" : "Refresh Token Exchange",
                        "ID_TOKEN" : "Identity Token Issued",
                        "UPDATE_PROFILE" : "Update Profile",
                        "UNKNOWN" : "Unknown"
                    }
                },
                "accountHealth": {
                    "undefined" : "...",
                    "UNKNOWN" : "Unknown",
                    "HEALTHY" : "Healthy",
                    "WARNING" : "Warning",
                    "SEVERE" : "Severe",
                },
                "globalAccountType": {
                    "EMPLOYEE" : "Employee",
                    "ADVISOR" : "Advisor",
                    "CUSTOMER" : "Customer",
                    "UNKNOWN" : "Unknown"
                },
                "rules" : {
                    "ACCOUNT_HAS_ANY_CHILDREN_RULE" : "No child records found",
                    "ACCOUNT_HAS_ANY_PRODUCTS_RULE" : "No products found",
                    "ACCOUNT_HAS_AN_ACTIVE_MEMBERSHIP" : "No active membership found for account - or the backed doesn't yet provide this information",
                    "AZURE_ACCOUNT_PHONE_NUMBER_AND_LOCAL_ACCOUNT_MATCHES" : "Identity provider and regional phone numbers dont match.",
                    "USER_HAS_SIGNED_IN_TO_ANY_SERVICE" : "No sign in activity detected for 7 days",
                    "IDENTITY_PROVIDER_ACCOUNT_EXISTS_RULE" : "The customer has never signed in",
                    "REGIONAL_ACCOUNT_EXISTS_RULE" : "No account found in the regional backend",
                    "NOT_A_WFIL_ACCOUNT" : "The account is a fake WFIL account",
                    "MULTIPLE_IDENTITY_PROVIDER_ACCOUNTS_FOUND" : "Multiple identity provider accounts map to the same regional account."

                },
                "membership" : {
                    "status" : {
                        "undefined" : "...",
                        "NONE" : "None/Unknown",
                        "ACTIVE" : "Active",
                        "SUSPENDED" : "Suspended",
                        "CANCELLED" : "Cancelled",
                        "EXPIRED" : "Expired"
                    },
                    "since" : "Since",
                    "expiry" : "Expiry",
                    "features" : "Membership Features"
                }
            },
            "views" : {
                "home" : {
                    "lookupByAccountPanel" : {
                        "title" : "Lookup By Account Number"
                    },
                    "lookupByPhonePanel" : {
                        "title" : "Lookup By Phone Number"
                    },
                },
                "lookupCustomerAccountView" : {
                    "accountHealthPanel" : {
                        "noProblemsDetectedTooltip" : "No problems detected"
                    },
                    "identityProviderAccountPanel": {
                        "deleteAccountDialogTitle" : "Delete Identity Provider Account?",
                        "deleteAccountDialogPrompt" : "This will not affect the regional account, but may cause issues for users signed in with that account.",
                        "invalidateSignInButton" : "Invalidate Sign Ins",
                        "invalidateSignInDialogTitle" : "Invalidate All User Sign Ins?",
                        "invalidateSignInDialogPrompt" : "This will invalidate the sign in sessions for the user across all applications.\nIf their devices are online, they will be forced to sign in again.\n\nThis may take up to 24 hours to take effect.",
                        "invalidateDialogActionButton" : "Invalidate"

                    },
                    "signActivityPanel" : {
                        "title" : "Sign In Activity (Last 90 Days)"
                    }
                },
                "regionalAccountDisplay" : {
                    "coreAccountDetailsSection": {
                        "title" : "Core Account Details"
                    }
                }
            }
        }
    },
    "ja-JP": {
        translation: {
            "applicationName": "CALI - 顧客アカウント検索サービス",
            "common" : {
                "lookup" : "検索",
                "clear" : "キャンセル",
                "locale" : "言語",
                "delete"  : "削除",
                "cancel" : "取消"
            },
            "domain" : {
                "common" : {
                    "region" : "地域",
                    "customerAccountNumber" : "顧客アカウント番号",
                    "customerPhoneNumber" : "顧客電話番号",
                    "accountNumber" : "アカウント番号",
                    "regionalAccount" : "地域アカウント",
                    "identityProvider" : "ID プロバイダー",
                    "signIn" : "サインイン",
                    "children" : "子供",
                    "products" : "製品",
                    "displayName" : "表示名",
                    "affinity" : "バックエンド",
                    "created" : "作成日",
                    "globalAccountId" : "グローバル アカウント ID",
                    "accountHealth" : "アカウントの健全性",
                    "signInSessionsValidFrom" : "セッションは時間から有効です",
                    "membership" : "メンバーシップ",
                    "bonusEntitlements" : "ボーナス特典"
                },
                "productLines" : {
                    "STEP_BY_STEP" : "ステップ・バイ・ステップ",
                    "LETS_PLAY" : "レッツ・プレイ",
                    "STRAIGHT_PLAY" : "ストレート・プレイ",
                    "PLAY_ALONG" : "プレイ・アロング",
                    "LETS_READ" : "Let's Read",
                    "SING_ALONG"  : "シング・アロング",
                    "ENGLISH_TAB" : "English Tab",

                    "PLAY_ALONG_AUDIO" : "プレイ・アロング！（オーディオ）",
                    "SING_ALONG_AUDIO" : "シング・アロング！（オーディオ）",

                    "SING_ALONG_SPOKEN_WORD_AUDIO" : "シング・アロング！（スポークン・ワード・オーディオ）",
                    "STORIES_AND_SONGS_AUDIO" : "ストーリーズ・アンド・ソングス（オーディオ）",

                    "BEDTIME_REVIEW" : "ベッドタイム・レビュー",

                    "BEDTIME_REVIEW_AUDIO" : "ベッドタイム・レビュー（オーディオ）",
                    "LISTEN_ALONG_AUDIO" : "リッスン・アロング（オーディオ）",

                    "STEP_BY_STEP_TEASER" : "ステップ・バイ・ステップ（ティーザー）",
                    "STRAIGHT_PLAY_TEASER" : "ストレート・プレイ（ティーザー）",
                    "LETS_PLAY_TEASER" : "レッツ・プレイ（ティーザー）"
                },
                "signInActivity" : {
                    "id": "ID",
                    "activity" : "アクティビティ",
                    "ipAddress" : "IP アドレス",
                    "timestamp" : "タイムスタンプ",
                    "appId" : "アプリケーション ID",
                    "appName" : "アプリケーション名",
                    "daysAgo" : "数日前",
                    "activities" : {
                        "ADD_USER" : "ユーザーの作成",
                        "UPDATE_PROFILE" : "プロフィールの更新",
                        "AUTHENTICATION" : "認証",
                        "ACCESS_TOKEN" : "アクセス トークンが発行されました",
                        "ACCESS_TOKEN_EXCHANGE" : "アクセス トークンの交換",
                        "REFRESH_TOKEN_EXCHANGE" : "リフレッシュ トークンの交換",
                        "ID_TOKEN" : "アイデンティティトークン発行",
                        "UNKNOWN" : "不明"
                    }
                },
                "accountHealth": {
                    "undefined" : "...",
                    "UNKNOWN" : "不明",
                    "HEALTHY" : "正常",
                    "WARNING" : "警告",
                    "SEVERE" : "重大",
                },
                "globalAccountType": {
                    "EMPLOYEE" : "従業員",
                    "ADVISOR" : "アドバイザー",
                    "CUSTOMER" : "顧客",
                    "UNKNOWN" : "不明"
                },
                "rules" : {
                    "ACCOUNT_HAS_ANY_CHILDREN_RULE" : "子レコードが見つかりません",
                    "ACCOUNT_HAS_ANY_PRODUCTS_RULE" : "製品が見つかりません」",
                    "ACCOUNT_HAS_AN_ACTIVE_MEMBERSHIP" : "アカウントにアクティブなメンバーシップが見つかりません",
                    "AZURE_ACCOUNT_PHONE_NUMBER_AND_LOCAL_ACCOUNT_MATCHES" : "IDプロバイダーと地域の電話番号が一致しません。",
                    "USER_HAS_SIGNED_IN_TO_ANY_SERVICE" : "7 日間サインイン アクティビティが検出されませんでした",
                    "IDENTITY_PROVIDER_ACCOUNT_EXISTS_RULE" : "顧客はサインインしたことがありません",
                    "REGIONAL_ACCOUNT_EXISTS_RULE" : "地域バックエンドにアカウントが見つかりません",
                    "NOT_A_WFIL_ACCOUNT" : "アカウントは偽の WFIL アカウントです",
                    "MULTIPLE_IDENTITY_PROVIDER_ACCOUNTS_FOUND" : "複数の ID プロバイダー アカウントが同じリージョン アカウントにマップされています。"
                },
                "membership" : {
                    "status" : {
                        "undefined" : "...",
                        "NONE" : "なし",
                        "ACTIVE" : "アクティブ",
                        "SUSPENDED" : "一時停止",
                        "CANCELLED" : "キャンセル",
                        "EXPIRED" : "期限切れ"
                    },
                    "since" : "開始日",
                    "expiry" : "有効期限",
                    "features" : "メンバーシップ機能"
            }
            },
            "views" : {
                "home" : {
                    "lookupByAccountPanel" : {
                        "title" : "口座番号で検索"
                    },
                    "lookupByPhonePanel" : {
                        "title" : "電話番号で検索"
                    }
                },
                "lookupCustomerAccountView" : {
                    "accountHealthPanel" : {
                        "noProblemsDetectedTooltip" : "問題は検出されませんでした"
                    },
                    "identityProviderAccountPanel": {
                        "deleteAccountDialogTitle" : "アイデンティティプロバイダーアカウントを削除しますか？",
                        "deleteAccountDialogPrompt" : "これにより地域アカウントには影響しませんが、そのアカウントでサインインしているユーザーに問題が発生する可能性があります。",
                        "invalidateSignInButton" : "サインインを無効化",
                        "invalidateSignInDialogTitle" : "すべてのユーザーサインインを無効化しますか？",
                        "invalidateSignInDialogPrompt" : "これにより、ユーザーのすべてのアプリケーションでのサインインセッションが無効になります。\nデバイスがオンラインの場合、再度サインインが求められます。\n\nこの処理が反映されるまで最大24時間かかる場合があります。",
                        "invalidateDialogActionButton" : "無効化"
                    },
                    "signActivityPanel" : {
                        "title" : "サインインアクティビティ（過去 90 日間）"
                    },
                },
                "regionalAccountDisplay" : {
                    "coreAccountDetailsSection": {
                        "title" : "コアアカウントの詳細"
                    }
                }
            }
        }
    },
    "ko-KR": {
        translation: {
            "applicationName": "CALI - 고객 계정 조회 인터페이스",
            "common" : {
                "lookup" : "조회",
                "clear" : "지우기",
                "locale" : "지역",
                "delete"  : "삭제",
                "cancel" : "취소"
            },
            "domain" : {
                "common" : {
                    "region" : "지역",
                    "customerAccountNumber" : "고객 계정 번호",
                    "customerPhoneNumber" : "고객 전화 번호",
                    "accountNumber" : "계정 번호",
                    "regionalAccount" : "지역 계정",
                    "identityProvider" : "ID 제공자",
                    "signIn" : "로그인",
                    "children" : "자녀",
                    "products" : "제품",
                    "displayName" : "표시 이름",
                    "affinity" : "백엔드",
                    "created" : "생성됨",
                    "globalAccountId" : "글로벌 계정 ID",
                    "accountHealth" : "계정 상태",
                    "signInSessionsValidFrom" : "세션은 시간부터 유효합니다",
                    "membership" : "멤버십",
                    "bonusEntitlements" : "보너스 자격"
                },
                "productLines": {
                    "STEP_BY_STEP": "단계별 진행",
                    "LETS_PLAY": "놀아보자",
                    "STRAIGHT_PLAY" : "스트레이트 플레이",
                    "PLAY_ALONG" : "함께 놀아요!",
                    "LETS_READ" : "읽어보자",
                    "SING_ALONG"  : "함께 노래해요",
                    "ENGLISH_TAB" : "영어 탭",

                    "PLAY_ALONG_AUDIO": "함께 놀아요! (오디오)",
                    "SING_ALONG_AUDIO": "함께 노래해요 (오디오)",

                    "SING_ALONG_SPOKEN_WORD_AUDIO": "함께 노래해요 (낭독 오디오)",
                    "STORIES_AND_SONGS_AUDIO": "이야기와 노래 (오디오)",

                    "BEDTIME_REVIEW": "취침 전 복습",

                    "BEDTIME_REVIEW_AUDIO": "취침 전 복습 (오디오)",
                    "LISTEN_ALONG_AUDIO": "함께 들어요 (오디오)",

                    "STEP_BY_STEP_TEASER": "단계별 진행 (맛보기)",
                    "STRAIGHT_PLAY_TEASER": "스트레이트 플레이 (맛보기)",
                    "LETS_PLAY_TEASER": "놀아보자 (맛보기)"
                },
                "signInActivity" : {
                    "id": "ID",
                    "activity" : "활동",
                    "ipAddress" : "IP 주소",
                    "timestamp" : "타임스탬프",
                    "appId" : "애플리케이션 ID",
                    "appName" : "애플리케이션 이름",
                    "daysAgo" : "며칠 전",
                    "activities" : {
                        "ADD_USER" : "사용자 생성",
                        "UPDATE_PROFILE" : "프로필 업데이트",
                        "AUTHENTICATION" : "인증",
                        "ACCESS_TOKEN" : "액세스 토큰 발급",
                        "ACCESS_TOKEN_EXCHANGE" : "액세스 토큰 교환",
                        "REFRESH_TOKEN_EXCHANGE" : "리프레시 토큰 교환",
                        "ID_TOKEN" : "ID 토큰 발급됨",
                        "UNKNOWN" : "알 수 없음"
                    }
                },
                "accountHealth": {
                    "undefined" : "...",
                    "UNKNOWN" : "알 수 없음",
                    "HEALTHY" : "양호",
                    "WARNING" : "경고",
                    "SEVERE" : "심각"
                },
                "globalAccountType": {
                    "EMPLOYEE" : "직원",
                    "ADVISOR" : "어드바이저",
                    "CUSTOMER" : "고객",
                    "UNKNOWN" : "알 수 없음"
                },
                "rules" : {
                    "ACCOUNT_HAS_ANY_CHILDREN_RULE" : "자녀 기록을 찾을 수 없음",
                    "ACCOUNT_HAS_ANY_PRODUCTS_RULE" : "제품을 찾을 수 없음",
                    "ACCOUNT_HAS_AN_ACTIVE_MEMBERSHIP" : "계정에 대한 활성 멤버십을 찾을 수 없습니다. 또는 백업에서 아직 이 정보를 제공하지 않습니다.",
                    "AZURE_ACCOUNT_PHONE_NUMBER_AND_LOCAL_ACCOUNT_MATCHES": "ID 공급자와 지역 전화번호가 일치하지 않습니다.",
                    "USER_HAS_SIGNED_IN_TO_ANY_SERVICE" : "최근 7일 동안 로그인 활동이 감지되지 않음",
                    "IDENTITY_PROVIDER_ACCOUNT_EXISTS_RULE" : "고객이 로그인한 기록이 없음",
                    "REGIONAL_ACCOUNT_EXISTS_RULE" : "지역 백엔드에서 계정을 찾을 수 없음",
                    "NOT_A_WFIL_ACCOUNT" : "계정이 가짜 WFIL 계정임",
                    "MULTIPLE_IDENTITY_PROVIDER_ACCOUNTS_FOUND": "여러 ID 공급자 계정이 동일한 지역 계정에 매핑됩니다."
                },
                "membership" : {
                    "status" : {
                        "undefined" : "...",
                        "NONE" : "없음/알 수 없음",
                        "ACTIVE" : "활성",
                        "SUSPENDED" : "일시 중지됨",
                        "CANCELLED" : "취소됨",
                        "EXPIRED" : "만료됨"
                    },
                    "since" : "시작",
                    "expiry" : "만료",
                    "features" : "멤버십 기능"
                }
            },
            "views" : {
                "home" : {
                    "lookupByAccountPanel" : {
                        "title" : "계정 번호로 조회"
                    },
                    "lookupByPhonePanel" : {
                        "title" : "전화 번호로 조회"
                    }
                },
                "lookupCustomerAccountView" : {
                    "accountHealthPanel" : {
                        "noProblemsDetectedTooltip" : "문제가 감지되지 않음"
                    },
                    "identityProviderAccountPanel": {
                        "deleteAccountDialogTitle" : "ID 제공자 계정을 삭제하시겠습니까?",
                        "deleteAccountDialogPrompt" : "이 작업은 지역 계정에 영향을 주지 않지만, 해당 계정으로 로그인한 사용자에게 문제가 발생할 수 있습니다.",
                        "invalidateSignInButton" : "로그인 무효화",
                        "invalidateSignInDialogTitle" : "모든 사용자 로그인을 무효화하시겠습니까?",
                        "invalidateSignInDialogPrompt" : "이 작업은 모든 애플리케이션에서 사용자의 로그인 세션을 무효화합니다.\n사용자의 기기가 온라인 상태라면 다시 로그인해야 합니다.\n\n이 작업이 완료되는 데 최대 24시간이 소요될 수 있습니다.",
                        "invalidateDialogActionButton" : "무효화"
                    },
                    "signActivityPanel" : {
                        "title" : "로그인 활동 (최근 90일)"
                    },
                },
                "regionalAccountDisplay" : {
                    "coreAccountDetailsSection": {
                        "title" : "핵심 계정 세부 정보"
                    }
                }
            }
        }

    },
    "zh-HK": {
        translation: {
            "applicationName": "CALI - 客戶賬戶查詢界面",
            "common" : {
                "lookup" : "查詢",
                "clear" : "清除",
                "locale" : "地區",
                "delete"  : "刪除",
                "cancel" : "取消"
            },
            "domain" : {
                "common" : {
                    "region" : "地區",
                    "customerAccountNumber" : "客戶賬戶號碼",
                    "customerPhoneNumber" : "客戶電話號碼",
                    "accountNumber" : "賬戶號碼",
                    "regionalAccount" : "地區賬戶",
                    "identityProvider" : "身份驗證提供者",
                    "signIn" : "登入",
                    "children" : "子女",
                    "products" : "產品",
                    "displayName" : "顯示名稱",
                    "affinity" : "後台",
                    "created" : "已創建",
                    "globalAccountId" : "全球賬戶ID",
                    "accountHealth" : "賬戶狀態",
                    "signInSessionsValidFrom" : "會話有效時間",
                    "membership" : "會員資格",
                    "bonusEntitlements" : "獎金權益"
                },
                "productLines": {
                    "STEP_BY_STEP": "逐步指南",
                    "LETS_PLAY": "來玩吧",
                    "STRAIGHT_PLAY" : "直接遊玩",
                    "PLAY_ALONG" : "一起玩!",
                    "LETS_READ" : "來讀書",
                    "SING_ALONG"  : "一起唱",
                    "ENGLISH_TAB" : "英語選項",

                    "PLAY_ALONG_AUDIO": "一齊玩！（音頻）",
                    "SING_ALONG_AUDIO": "一齊唱！（音頻）",

                    "SING_ALONG_SPOKEN_WORD_AUDIO": "一齊唱！（朗讀音頻）",
                    "STORIES_AND_SONGS_AUDIO": "故事與歌曲（音頻）",

                    "BEDTIME_REVIEW": "睡前溫習",

                    "BEDTIME_REVIEW_AUDIO": "睡前溫習（音頻）",
                    "LISTEN_ALONG_AUDIO": "一齊聆聽（音頻）",

                    "STEP_BY_STEP_TEASER": "逐步指引（預告）",
                    "STRAIGHT_PLAY_TEASER": "直接遊玩（預告）",
                    "LETS_PLAY_TEASER": "來玩吧（預告）"
                },
                "signInActivity" : {
                    "id": "ID",
                    "activity" : "活動",
                    "ipAddress" : "IP位址",
                    "timestamp" : "時間戳",
                    "appId" : "應用程式 ID",
                    "appName" : "應用程式名稱",
                    "daysAgo" : "天前",
                    "activities" : {
                        "ADD_USER" : "建立使用者",
                        "UPDATE_PROFILE" : "更新個人檔案",
                        "AUTHENTICATION" : "認證",
                        "ACCESS_TOKEN" : "已發行存取權杖",
                        "ACCESS_TOKEN_EXCHANGE" : "存取權杖交換",
                        "REFRESH_TOKEN_EXCHANGE" : "刷新權杖交換",
                        "ID_TOKEN" : "身份令牌已發行",
                        "UNKNOWN" : "未知"
                    }
                },
                "accountHealth": {
                    "undefined" : "...",
                    "UNKNOWN" : "未知",
                    "HEALTHY" : "健康",
                    "WARNING" : "警告",
                    "SEVERE" : "嚴重"
                },
                "globalAccountType": {
                    "EMPLOYEE" : "員工",
                    "ADVISOR" : "顧問",
                    "CUSTOMER" : "客戶",
                    "UNKNOWN" : "未知"
                },
                "rules" : {
                    "ACCOUNT_HAS_ANY_CHILDREN_RULE" : "未找到子女記錄",
                    "ACCOUNT_HAS_ANY_PRODUCTS_RULE" : "未找到產品",
                    "ACCOUNT_HAS_AN_ACTIVE_MEMBERSHIP" : "未找到該帳戶的有效會員資格 - 或支援尚未提供此資訊",
                    "AZURE_ACCOUNT_PHONE_NUMBER_AND_LOCAL_ACCOUNT_MATCHES" : "身分提供者和區域電話號碼不符。",
                    "USER_HAS_SIGNED_IN_TO_ANY_SERVICE" : "7天內未偵測到登入活動",
                    "IDENTITY_PROVIDER_ACCOUNT_EXISTS_RULE" : "該客戶從未登入",
                    "REGIONAL_ACCOUNT_EXISTS_RULE" : "在區域後台中未找到帳戶",
                    "NOT_A_WFIL_ACCOUNT" : "該帳戶為虛假WFIL帳戶",
                    "MULTIPLE_IDENTITY_PROVIDER_ACCOUNTS_FOUND" : "多個身分提供者帳戶對應到同一個區域帳戶。"
                },
                "membership" : {
                    "status" : {
                        "undefined" : "...",
                        "NONE" : "無/未知",
                        "ACTIVE" : "活動",
                        "SUSPENDED" : "已暫停",
                        "CANCELLED" : "已取消",
                        "EXPIRED" : "已過期"
                    },
                    "since" : "開始",
                    "expiry" : "已過期",
                    "features" : "會員功能"
                }
            },
            "views" : {
                "home" : {
                    "lookupByAccountPanel" : {
                        "title" : "按賬戶號碼查詢"
                    },
                    "lookupByPhonePanel" : {
                        "title" : "按電話號碼查詢"
                    }
                },
                "lookupCustomerAccountView" : {
                    "accountHealthPanel" : {
                        "noProblemsDetectedTooltip" : "未檢測到問題"
                    },
                    "identityProviderAccountPanel": {
                        "deleteAccountDialogTitle" : "刪除身份驗證提供者賬戶?",
                        "deleteAccountDialogPrompt" : "此操作不會影響地區賬戶，但可能會對使用該賬戶登入的用戶造成影響。",
                        "invalidateSignInButton" : "使登入無效",
                        "invalidateSignInDialogTitle" : "將所有使用者的登入設為無效?",
                        "invalidateSignInDialogPrompt" : "此操作將使該使用者在所有應用程式中的登入會話無效。\n如果他們的設備處於在線狀態，他們將被迫重新登入。\n\n此操作可能需要最多24小時生效。",
                        "invalidateDialogActionButton" : "設為無效"
                    },
                    "signActivityPanel" : {
                        "title" : "登入活動 (最近90天)"
                    }
                },
                "regionalAccountDisplay" : {
                    "coreAccountDetailsSection": {
                        "title" : "核心帳戶詳情"
                    }
                }
            }
        }

    },
    "zh-TW": {
        translation: {
            "applicationName": "CALI - 客戶帳戶查詢介面",
            "common" : {
                "lookup" : "查詢",
                "clear" : "清除",
                "locale" : "區域",
                "delete"  : "刪除",
                "cancel" : "取消"
            },
            "domain" : {
                "common" : {
                    "region" : "區域",
                    "customerAccountNumber" : "客戶帳戶號碼",
                    "customerPhoneNumber" : "客戶電話號碼",
                    "accountNumber" : "帳戶號碼",
                    "regionalAccount" : "區域帳戶",
                    "identityProvider" : "身份驗證提供者",
                    "signIn" : "登入",
                    "children" : "子女",
                    "products" : "產品",
                    "displayName" : "顯示名稱",
                    "affinity" : "後台",
                    "created" : "已創建",
                    "globalAccountId" : "全球帳戶ID",
                    "accountHealth" : "帳戶狀態",
                    "signInSessionsValidFrom" : "會話有效時間",
                    "membership" : "會員資格",
                    "bonusEntitlements" : "獎金權益"
                },
                "productLines": {
                    "STEP_BY_STEP": "逐步指南",
                    "LETS_PLAY": "來玩吧",
                    "STRAIGHT_PLAY" : "直接遊玩",
                    "PLAY_ALONG" : "一起玩!",
                    "LETS_READ" : "來讀書",
                    "SING_ALONG"  : "一起唱",
                    "ENGLISH_TAB" : "英文選項",

                    "PLAY_ALONG_AUDIO": "一起玩！（音訊）",
                    "SING_ALONG_AUDIO": "一起唱！（音訊）",

                    "SING_ALONG_SPOKEN_WORD_AUDIO": "一起唱！（朗讀音訊）",
                    "STORIES_AND_SONGS_AUDIO": "故事與歌曲（音訊）",

                    "BEDTIME_REVIEW": "睡前複習",

                    "BEDTIME_REVIEW_AUDIO": "睡前複習（音訊）",
                    "LISTEN_ALONG_AUDIO": "一起聆聽（音訊）",

                    "STEP_BY_STEP_TEASER": "逐步指南（預告）",
                    "STRAIGHT_PLAY_TEASER": "直接遊玩（預告）",
                    "LETS_PLAY_TEASER": "來玩吧（預告）"
                },
                "signInActivity" : {
                    "id": "ID",
                    "activity" : "活動",
                    "ipAddress" : "IP位址",
                    "timestamp" : "時間戳",
                    "appId" : "應用程序 ID",
                    "appName" : "應用程序名稱",
                    "daysAgo" : "天前",
                    "activities" : {
                        "ADD_USER" : "建立使用者",
                        "UPDATE_PROFILE" : "更新個人檔案",
                        "AUTHENTICATION" : "認證",
                        "ACCESS_TOKEN" : "已發行存取權杖",
                        "ACCESS_TOKEN_EXCHANGE" : "存取權杖交換",
                        "REFRESH_TOKEN_EXCHANGE" : "刷新權杖交換",
                        "ID_TOKEN" : "身份令牌已發行",
                        "UNKNOWN" : "未知"
                    }
                },
                "accountHealth": {
                    "undefined" : "...",
                    "UNKNOWN" : "未知",
                    "HEALTHY" : "健康",
                    "WARNING" : "警告",
                    "SEVERE" : "嚴重"
                },
                "globalAccountType": {
                    "EMPLOYEE" : "員工",
                    "ADVISOR" : "顧問",
                    "CUSTOMER" : "客戶",
                    "UNKNOWN" : "未知"
                },
                "rules" : {
                    "ACCOUNT_HAS_ANY_CHILDREN_RULE" : "未找到子女記錄",
                    "ACCOUNT_HAS_ANY_PRODUCTS_RULE" : "未找到產品",
                    "ACCOUNT_HAS_AN_ACTIVE_MEMBERSHIP" : "未找到該帳戶的有效會員資格 - 或支援尚未提供此資訊",
                    "AZURE_ACCOUNT_PHONE_NUMBER_AND_LOCAL_ACCOUNT_MATCHES" : "身分提供者和區域電話號碼不符。",
                    "USER_HAS_SIGNED_IN_TO_ANY_SERVICE" : "7天內未偵測到登入活動",
                    "IDENTITY_PROVIDER_ACCOUNT_EXISTS_RULE" : "該客戶從未登入",
                    "REGIONAL_ACCOUNT_EXISTS_RULE" : "在區域後台中未找到帳戶",
                    "NOT_A_WFIL_ACCOUNT" : "該帳戶為虛假WFIL帳戶",
                    "MULTIPLE_IDENTITY_PROVIDER_ACCOUNTS_FOUND" : "多個身分提供者帳戶對應到同一個區域帳戶。"
                },
                "membership" : {
                    "status" : {
                        "undefined" : "...",
                        "NONE" : "無/未知",
                        "ACTIVE" : "活動",
                        "SUSPENDED" : "已暫停",
                        "CANCELLED" : "已取消",
                        "EXPIRED" : "已過期"
                    },
                    "since" : "開始",
                    "expiry" : "已過期",
                    "features" : "會員功能"
                }
            },
            "views" : {
                "home" : {
                    "lookupByAccountPanel" : {
                        "title" : "按帳戶號碼查詢"
                    },
                    "lookupByPhonePanel" : {
                        "title" : "按電話號碼查詢"
                    }
                },
                "lookupCustomerAccountView" : {
                    "accountHealthPanel" : {
                        "noProblemsDetectedTooltip" : "未檢測到問題"
                    },
                    "identityProviderAccountPanel": {
                        "deleteAccountDialogTitle" : "刪除身份驗證提供者帳戶?",
                        "deleteAccountDialogPrompt" : "此操作不會影響區域帳戶，但可能會對使用該帳戶登入的用戶造成影響。",
                        "invalidateSignInButton" : "使登入無效",
                        "invalidateSignInDialogTitle" : "將所有使用者的登入設為無效?",
                        "invalidateSignInDialogPrompt" : "此操作將使該使用者在所有應用程序中的登入會話無效。\n如果他們的設備處於在線狀態，他們將被迫重新登入。\n\n此操作可能需要最多24小時生效。",
                        "invalidateDialogActionButton" : "設為無效"
                    },
                    "signActivityPanel" : {
                        "title" : "登入活動 (最近90天)"
                    }
                },
                "regionalAccountDisplay" : {
                    "coreAccountDetailsSection": {
                        "title" : "核心帳戶詳情"
                    }
                }
            }
        }

    }
}

i18n
    .use(initReactI18next) // passes i18n down to react-i18next
    .init({
        resources,
        lng: DEFAULT_LOCALE, // language to use, more information here: https://www.i18next.com/overview/configuration-options#languages-namespaces-resources
        fallbackLng: DEFAULT_LOCALE,
        // you can use the i18n.changeLanguage function to change the language manually: https://www.i18next.com/overview/api#changelanguage
        // if you're using a language detector, do not define the lng option

        interpolation: {
            escapeValue: false // react already safes from xss
        }
    });

export default i18n;
